import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ASSETS } from "@/data";
import { CTAButton, requestInquiry, scrollToId } from "@/components/site/ui";

const NAV = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Immigration Services", id: "services" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => go("home")}
          data-testid="logo-home-btn"
          className="flex items-center gap-4"
        >
          <img src={ASSETS.logo} alt="Sleiman Immigration" className="h-9 md:h-10 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => go(n.id)}
              className="text-sm text-charcoal/80 hover:text-crimson transition-colors duration-200 relative group"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-crimson transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden lg:block">
          <CTAButton
            testid="header-book-btn"
            onClick={() => requestInquiry("consultation")}
            className="!py-3 !px-6"
          >
            Book a Consultation
          </CTAButton>
        </div>

        <button
          className="lg:hidden text-charcoal"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-b border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  data-testid={`mobile-nav-${n.id}`}
                  onClick={() => go(n.id)}
                  className="text-left text-lg text-charcoal hover:text-crimson transition-colors"
                >
                  {n.label}
                </button>
              ))}
              <CTAButton
                testid="mobile-book-btn"
                onClick={() => {
                  setOpen(false);
                  requestInquiry("consultation");
                }}
                className="mt-2 justify-center"
              >
                Book a Consultation
              </CTAButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
